/**
 * AI provider presets — common providers, endpoints, and models.
 * Users can still type custom values; these are suggestions.
 */

export const PROVIDER_PRESETS = [
  { value: "openai", label: "OpenAI" },
  { value: "anthropic", label: "Anthropic" },
  { value: "xai", label: "xAI (Grok)" },
  { value: "google", label: "Google (Gemini)" },
  { value: "openrouter", label: "OpenRouter (Multi-provider)" },
  { value: "ollama", label: "Ollama (Local)" },
  { value: "custom", label: "Custom" },
];

export const ENDPOINT_PRESETS = {
  openai: "https://api.openai.com/v1/chat/completions",
  anthropic: "https://api.anthropic.com/v1/messages",
  xai: "https://api.x.ai/v1/chat/completions",
  google: "https://generativelanguage.googleapis.com/v1beta/openai/chat/completions",
  openrouter: "https://openrouter.ai/api/v1/chat/completions",
  ollama: "http://localhost:11434/v1/chat/completions",
};

export const MODEL_PRESETS = {
  openai: [
    { value: "gpt-5.5", label: "GPT-5.5 (Latest flagship)" },
    { value: "gpt-5.5-pro", label: "GPT-5.5 Pro (Highest accuracy)" },
    { value: "gpt-5.4", label: "GPT-5.4" },
    { value: "gpt-5.4-mini", label: "GPT-5.4 Mini (Fast)" },
    { value: "gpt-5.4-nano", label: "GPT-5.4 Nano (Cheapest)" },
    { value: "gpt-5.3-codex", label: "GPT-5.3 Codex (Coding)" },
    { value: "gpt-5.3", label: "GPT-5.3" },
    { value: "o3", label: "o3 (Reasoning)" },
    { value: "o3-mini", label: "o3-mini (Reasoning, fast)" },
    { value: "gpt-4o", label: "GPT-4o (Legacy)" },
    { value: "gpt-4o-mini", label: "GPT-4o Mini (Legacy)" },
  ],
  anthropic: [
    { value: "claude-sonnet-5", label: "Claude Sonnet 5 (Balanced)" },
    { value: "claude-opus-4-8", label: "Claude Opus 4.8 (Advanced coding)" },
    { value: "claude-fable-5", label: "Claude Fable 5 (Most capable)" },
    { value: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5 (Fastest)" },
    { value: "claude-sonnet-4-6", label: "Claude Sonnet 4.6 (Legacy)" },
    { value: "claude-opus-4-7", label: "Claude Opus 4.7 (Legacy)" },
    { value: "claude-opus-4-6", label: "Claude Opus 4.6 (Legacy)" },
    { value: "claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5 (Legacy)" },
    { value: "claude-opus-4-5-20251101", label: "Claude Opus 4.5 (Legacy)" },
    { value: "claude-sonnet-4-20250514", label: "Claude Sonnet 4 (Legacy)" },
    { value: "claude-opus-4-20250514", label: "Claude Opus 4 (Legacy)" },
  ],
  xai: [
    { value: "grok-4-1-fast-non-reasoning", label: "Grok 4.1 Fast (Instant)" },
    { value: "grok-4-1-fast-reasoning", label: "Grok 4.1 Fast (Reasoning)" },
    { value: "grok-4.20-0309-non-reasoning", label: "Grok 4.20 (Non-reasoning)" },
    { value: "grok-4.20-0309-reasoning", label: "Grok 4.20 (Reasoning)" },
    { value: "grok-3", label: "Grok 3" },
    { value: "grok-3-mini", label: "Grok 3 Mini" },
    { value: "grok-code-fast-1", label: "Grok Code Fast (Coding)" },
  ],
  google: [
    { value: "gemini-3-flash", label: "Gemini 3 Flash (Latest — Pro intelligence at Flash price)" },
    { value: "gemini-3.1-pro-preview", label: "Gemini 3.1 Pro (Preview)" },
    { value: "gemini-3.1-flash-lite", label: "Gemini 3.1 Flash Lite (High-volume)" },
    { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
    { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
    { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite" },
  ],
  // One key, every major vendor. IDs are OpenRouter's namespaced slugs
  // (vendor/model) — verified against https://openrouter.ai/api/v1/models
  // 2026-08-27. Users can type any other slug from the catalog.
  openrouter: [
    { value: "anthropic/claude-sonnet-5", label: "Claude Sonnet 5 (Balanced)" },
    { value: "anthropic/claude-haiku-4.5", label: "Claude Haiku 4.5 (Fast)" },
    { value: "google/gemini-3.7-flash", label: "Gemini 3.7 Flash (Latest)" },
    { value: "google/gemini-3.1-pro-preview", label: "Gemini 3.1 Pro (Preview)" },
    { value: "openai/gpt-5.5", label: "GPT-5.5 (Flagship)" },
    { value: "openai/gpt-5.4-mini", label: "GPT-5.4 Mini (Fast)" },
    { value: "x-ai/grok-4.6", label: "Grok 4.6" },
    { value: "deepseek/deepseek-chat", label: "DeepSeek Chat (Value)" },
    { value: "meta-llama/llama-4-maverick", label: "Llama 4 Maverick (Open weights)" },
  ],
  ollama: [
    { value: "llama3.3", label: "Llama 3.3" },
    { value: "mistral", label: "Mistral" },
    { value: "codellama", label: "Code Llama" },
    { value: "deepseek-coder-v2", label: "DeepSeek Coder V2" },
    { value: "qwen2.5-coder", label: "Qwen 2.5 Coder" },
  ],
};

/** Get all models as a flat list for a combobox. */
export function getAllModelOptions() {
  const all = [];
  for (const [provider, models] of Object.entries(MODEL_PRESETS)) {
    const providerLabel = PROVIDER_PRESETS.find((p) => p.value === provider)?.label || provider;
    for (const m of models) {
      all.push({ value: m.value, label: `${m.label}`, group: providerLabel });
    }
  }
  return all;
}

/** Guess provider key from an endpoint URL. */
export function guessProviderFromEndpoint(endpoint) {
  if (!endpoint) return null;
  const e = endpoint.toLowerCase();
  // Before the vendor checks: OpenRouter serves every vendor's models from
  // its own host, so nothing below should shadow it.
  if (e.includes("openrouter.ai")) return "openrouter";
  if (e.includes("openai.com")) return "openai";
  if (e.includes("anthropic.com") || e.includes("claude.com")) return "anthropic";
  if (e.includes("x.ai")) return "xai";
  if (e.includes("googleapis.com") || e.includes("generativelanguage")) return "google";
  if (e.includes("localhost") || e.includes("127.0.0.1")) return "ollama";
  return null;
}
